import React, { useEffect, useState } from 'react';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons/faQuestionCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MentionsInput, Mention, SuggestionDataItem } from 'react-mentions';
import { SubmitHandler, useForm } from 'react-hook-form';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import ErrorBoundary from '@buyside/components/presentation/ErrorBoundary/errorBoundary';
import FileUploadWidget, {
  FileType
} from '@buyside/components/presentation/Form/FileUploadWidget/fileUploadWidget';
import ProfileImage, {
  IMAGE_SIZES
} from '@buyside/components/presentation/ProfileImage/profileImage';
import RatingArea from '@buyside/components/presentation/IdeaShowPage/Rating/ratingArea';
import TooltipWhite from '@buyside/components/presentation/Tooltips/TooltipWhite/tooltipWhite';
import useMentionableAsset from '@buyside/components/container/Mentionable/useMentionableAsset';

import {
  CreateIdeaCommentMutationVariables,
  IdeaCommentsDocument,
  IdeaCommentsQuery,
  Maybe,
  Mentionable,
  useCreateIdeaCommentMutation
} from '@interfaces/graphql-typings';

import styles from './showPageCommentFormStyles.scss';
import mentionStyling from '@buyside/styles/module/form/mentionStyling';

require('buyside/components/presentation/Chatterbox/chatterboxStyles');

interface ShowPageCommentFormProps {
  authorTag: string;
  canCommentAnonymously: boolean;
  canRateIdea: boolean;
  currentUserId: string;
  ideaId: string;
  isAllocator: boolean;
  isAuthor: boolean;
  isIdeaAnonymous: boolean;
  isEmployed: boolean;
  mentionables?: Maybe<Mentionable>[];
  profileImageUrl: Maybe<string>;
}

const ShowPageCommentForm: React.FC<ShowPageCommentFormProps> = ({
  authorTag,
  canCommentAnonymously,
  canRateIdea,
  ideaId,
  isAllocator,
  isAuthor,
  isIdeaAnonymous,
  mentionables = [],
  profileImageUrl
}): React.ReactElement => {
  const [mentionableAssets, markup, renderSuggestion, displayTransform] = useMentionableAsset();

  const [isFocused, setIsFocused] = useState(false);
  const [commentBody, setCommentBody] = useState('');

  const [createComment, { error, loading }] = useCreateIdeaCommentMutation({
    update(cache, { data }) {
      const newComment = data?.createComment?.comment;
      if (!newComment) return;

      const cachedQuery = cache.readQuery<IdeaCommentsQuery>({
        query: IdeaCommentsDocument,
        variables: { ideaId, controlName: !isAuthor }
      });
      if (!cachedQuery?.idea.comments) return;

      cache.writeQuery<IdeaCommentsQuery>({
        query: IdeaCommentsDocument,
        data: {
          idea: {
            ...cachedQuery.idea,
            commentsCount: (cachedQuery.idea.commentsCount || 0) + 1,
            comments: { edges: [{ node: newComment }, ...(cachedQuery.idea.comments.edges || [])] }
          }
        },
        variables: { ideaId }
      });
    }
  });

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    reset,
    setValue,
    watch
  } = useForm<
    Omit<CreateIdeaCommentMutationVariables, 'ideaId' | 'files'> & {
      ideaId?: string;
      files: FileType[];
    }
  >({
    defaultValues: {
      body: '',
      ideaId,
      parentId: null,
      anonymous: (isAllocator && !isAuthor) || (isAuthor && isIdeaAnonymous),
      response: false,
      files: []
    }
  });

  useEffect(() => {
    reset({
      body: '',
      ideaId,
      parentId: null,
      anonymous: (isAllocator && !isAuthor) || (isAuthor && isIdeaAnonymous),
      response: false,
      files: []
    });
  }, [reset, ideaId, isAllocator, isAuthor, isIdeaAnonymous]);

  const prepareFiles = (files: FileType[]): string => {
    const uploadedFiles = files.map(file => {
      const { name, base64 } = file;
      const fileData = file.file;
      return {
        name,
        fileName: fileData.name,
        size: fileData.size,
        data: base64
      };
    });
    return JSON.stringify(uploadedFiles);
  };

  const files = (watch('files') as unknown) as FileType[];

  const onSubmit: SubmitHandler<Omit<CreateIdeaCommentMutationVariables, 'ideaId' | 'files'> & {
    ideaId?: string;
    files: FileType[];
  }> = ({ anonymous, response }): void => {
    createComment({
      variables: {
        ideaId,
        body: commentBody,
        anonymous,
        response,
        files: prepareFiles(files)
      }
    }).then(() => {
      setCommentBody('');
      setIsFocused(false);
    });
  };

  const toggleResponse = (): void => {
    let addAuthorTag;
    const response = watch('response');
    if (response) {
      addAuthorTag = '';
    } else {
      addAuthorTag = commentBody.includes(authorTag) ? '' : authorTag;
    }

    setValue('response', !response);
    setCommentBody(commentBody + addAuthorTag);
    setValue('body', commentBody + addAuthorTag);
  };

  return (
    <ErrorBoundary pageName="comment form">
      <form
        className={styles.form}
        data-testid="idea-comment-form"
        onFocus={(): void => setIsFocused(true)}
        onSubmit={handleSubmit(onSubmit)}
      >
        {isFocused && canRateIdea && !isAuthor && (
          <div className={styles.ratingContainer}>
            <div>What do you think of this idea?</div>
            <div className={styles.ratingArea}>
              <RatingArea
                canRateIdea={canRateIdea}
                ideaId={ideaId}
                isAuthor={isAuthor}
                isPublic={false}
                forCommentForm
              />
              <TooltipWhite
                overlay="All ratings are anonymous. SumZero will never publicly associate your name
              with your rating."
                placement="top"
                mouseLeaveDelay={0}
                mouseEnterDelay={0.1}
              >
                <FontAwesomeIcon icon={faQuestionCircle} className={styles.questionIcon} />
              </TooltipWhite>
            </div>
          </div>
        )}
        <div className={styles.formContainer}>
          <div className={styles.formLeft}>
            <ProfileImage source={profileImageUrl} size={IMAGE_SIZES.SMALL} />
          </div>
          <div className={styles.formRight}>
            <MentionsInput
              {...register('body')}
              allowSpaceInQuery
              data-heap="comment-text-field"
              data-testid="showPageCommentTextArea"
              disabled={isSubmitting || loading}
              id="idea-comment-body"
              onChange={(e): void => {
                setCommentBody(e.target.value);
                setValue('body', e.target.value, { shouldDirty: true });
              }}
              placeholder="Post a comment"
              style={mentionStyling}
              value={commentBody}
            >
              <Mention
                appendSpaceOnAdd
                data={mentionables.filter(Boolean) as SuggestionDataItem[]}
                style={{ backgroundColor: '#DEEDF5' }}
                trigger="@"
              />
              <Mention
                appendSpaceOnAdd
                data={mentionableAssets}
                displayTransform={displayTransform}
                markup={markup}
                renderSuggestion={renderSuggestion}
                style={{ backgroundColor: 'rgb(255, 201, 201)' }}
                trigger="$"
              />
            </MentionsInput>
            {isFocused && (
              <>
                {errors.body && <div className={styles.error}>{errors.body.message}</div>}
                <FileUploadWidget
                  data-testid="fileUploadWidget"
                  setFieldValue={setValue}
                  uploadedFiles={files}
                />
                <div className={styles.fieldNote}>
                  <div>
                    <strong>Type '@'</strong> to tag participants in this comment thread, users you
                    follow, or users that are following you. Tagged members will be notified.
                  </div>
                  <div>
                    <strong>Type '$'</strong> and begin typing to tag any ticker on SumZero. Members
                    have watchlisted the tagged ticker will be notified.
                  </div>
                </div>
                <div>
                  {!isAuthor && (
                    <label className={styles.checkbox}>
                      <input
                        {...register('response')}
                        data-heap="request-response"
                        disabled={isSubmitting || loading}
                        id="request-response-from-author"
                        onChange={(): void => toggleResponse()}
                        type="checkbox"
                      />
                      Request a response from the author
                    </label>
                  )}
                  {((!isAuthor && !isAllocator && canCommentAnonymously) ||
                    (isAuthor && isIdeaAnonymous)) && (
                    <label className={styles.checkbox} id="post-comment-anonymously">
                      <input
                        {...register('anonymous')}
                        disabled={isSubmitting || loading}
                        id="idea-comment-anonymous"
                        type="checkbox"
                      />
                      Post this comment anonymously
                    </label>
                  )}
                </div>
                {error && <div className={styles.error}>{error.graphQLErrors[0].message}</div>}
              </>
            )}
            <div className={styles.buttonArea}>
              <Button
                analyticsTag="post-comment"
                disabled={!commentBody}
                id="post-idea-comment"
                isLoading={isSubmitting || loading}
                size={BUTTON_SIZES.SMALL}
                text="Post Comment"
                type={BUTTON_TYPES.PRIMARY}
              />
            </div>
            {!isAuthor && isAllocator && (
              <div className={styles.allocatorNotice}>
                As an allocator, all of your comments are anonymous.
              </div>
            )}
          </div>
        </div>
      </form>
    </ErrorBoundary>
  );
};

export default ShowPageCommentForm;
